// ============================================
// TIPOS COMUNS
// ============================================

export interface ApiResponse<T> {
  data?: T;
  error?: string;
  message?: string;
}

export interface PaginationParams {
  page?: number;
  limit?: number;
}

export interface PaginationResponse {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

// ============================================
// USUÁRIO E AUTENTICAÇÃO
// ============================================

export interface Address {
  id?: string;
  name: string;
  street: string;
  number: string;
  complement?: string;
  neighborhood: string;
  city: string;
  state: string;
  zipCode: string;
  country?: string;
  isDefault?: boolean;
}

export type UserRole = 'CUSTOMER' | 'ADMIN' | 'MANAGER' | 'OPERATOR';

export interface User {
  id: string;
  name: string;
  email: string;
  cpf: string;
  phone: string;
  birthDate?: string;
  addresses: Address[];
  createdAt: Date;
  updatedAt: Date;
  role?: UserRole;
}

export interface LoginRequest {
  emailOrCpf: string;
  password: string;
}

export interface LoginResponse {
  user: User;
  token: string;
}

export interface RegisterRequest {
  name: string;
  email: string;
  cpf: string;
  password: string;
  phone: string;
  birthDate: string | undefined;
}

export interface RegisterResponse {
  user: User;
  token: string;
}

// ============================================
// BATERIAS / PRODUTOS
// ============================================

export interface BatteryDimensions {
  length: number;
  width: number;
  height: number;
}

export interface BatterySpecification {
  name: string;
  value: string;
  unit?: string;
}

export interface VehicleCompatibility {
  brand: string;
  model: string;
  year: {
    from: number;
    to: number;
  };
}

export interface BatteryCategory {
  id: string;
  name: string;
  slug: string;
  imageUrl?: string;
}

export interface Battery {
  id: string;
  slug: string;
  name: string;
  brand?: string;
  model?: string;
  capacity: number;
  voltage: number;
  dimensions: BatteryDimensions;
  weight: number;
  price: number;
  originalPrice?: number;
  discount?: number;
  images: string[];
  description: string;
  specifications: BatterySpecification[];
  compatibleVehicles: VehicleCompatibility[];
  stock: number;
  isActive: boolean;
  /** Quando true, produto permite opção de troca de sucata (Campinas). */
  allowsScrapReturn?: boolean;
  /** Admin: override por produto (null = segue categoria). */
  scrapReturnEnabled?: boolean | null;
  category: BatteryCategory;
  tags: string[];
  rating: number;
  reviewCount: number;
  createdAt: Date;
  updatedAt: Date;
}

export interface BatteryFilters {
  search?: string;
  brand?: string[];
  capacity_min?: number;
  capacity_max?: number;
  voltage?: number[];
  price_min?: number;
  price_max?: number;
  category?: string[];
  inStock?: boolean;
  sortBy?: 'price' | 'name' | 'rating' | 'capacity';
  sortOrder?: 'asc' | 'desc';
  page?: number;
  limit?: number;
}

export interface BatteryListResponse {
  batteries: Battery[];
  pagination: PaginationResponse;
  filters: {
    brands: string[];
    categories: string[];
    priceRange: {
      min: number;
      max: number;
    };
  };
}

// ============================================
// CARRINHO
// ============================================

export interface CartItem {
  product: Battery;
  quantity: number;
}

export interface Cart {
  items: CartItem[];
  subtotal: number;
  total: number;
}

export interface AddToCartRequest {
  batteryId: string;
  quantity: number;
}

export interface UpdateCartRequest {
  quantity: number;
}

// ============================================
// CUPONS
// ============================================

export interface Coupon {
  id: string;
  code: string;
  name: string;
  description: string;
  type: 'percentage' | 'fixed' | 'free_shipping';
  value: number;
  minOrderValue?: number;
  maxDiscount?: number;
  usageLimit?: number;
  usedCount: number;
  validFrom: Date;
  validUntil: Date;
  isActive: boolean;
}

export interface ValidateCouponRequest {
  code: string;
  subtotal: number;
}

export interface ValidateCouponResponse {
  valid: boolean;
  coupon?: Coupon;
  discount: number;
  message: string;
}

export interface ApplyCouponRequest {
  code: string;
  cartItems: {
    batteryId: string;
    quantity: number;
    price: number;
  }[];
}

export interface ApplyCouponResponse {
  applied: boolean;
  coupon: Coupon;
  discount: number;
  newTotal: number;
}

// ============================================
// PEDIDOS
// ============================================

export enum OrderStatus {
  PENDING = 'pending',
  PROCESSING = 'processing',
  SHIPPED = 'shipped',
  DELIVERED = 'delivered',
  CANCELLED = 'cancelled',
  CONFIRMED = 'confirmed',
  RETURNED = 'returned',
  READY_FOR_PICKUP = 'ready_for_pickup',
}

export interface OrderItem {
  id: string;
  productName: string;
  productImage: string;
  quantity: number;
  price: number;
  brand: string;
  capacity: string;
  unitPrice?: number;
  totalPrice?: number;
  product?: {
    id?: string;
    name?: string;
    requiresShipping?: boolean;
    isDigital?: boolean;
    [key: string]: unknown;
  };
}

export interface OrderLog {
  id: string;
  status: string;
  previousStatus?: string | null;
  action: string;
  description?: string | null;
  performedBy?: string | null;
  metadata?: Record<string, unknown> | null;
  createdAt: string;
}

export interface Order {
  id: string;
  orderNumber: string;
  date: string;
  status: OrderStatus;
  items: OrderItem[];
  subtotal: number;
  shipping: number;
  discount: number;
  total: number;
  shippingAddress: Address;
  paymentMethod: PaymentMethod | string;
  trackingCode?: string;
  estimatedDelivery?: string;
  billingAddress?: Address;
  paymentStatus?: PaymentStatus | string;
  notes?: string;
  tracking?: TrackingEvent[];
  transactions?: PaymentTransaction[];
  logs?: OrderLog[];
  customerName?: string;
  customerEmail?: string;
  createdAt?: string;
  updatedAt?: string;
  shippingMethod?: string;
}

export interface CreateOrderRequest {
  items: {
    batteryId: string;
    quantity: number;
    price: number;
  }[];
  shippingAddress: Address;
  billingAddress: Address;
  paymentMethod: PaymentMethod;
  shippingMethod: string;
  appliedCoupons?: string[];
  subtotal: number;
  discount: number;
  shipping: number;
  total: number;
}

export interface CreateOrderResponse {
  order: Order;
  paymentUrl?: string;
}

export interface OrderListResponse {
  orders: Order[];
  pagination: PaginationResponse;
}

export interface AdminOrder extends Order {
  customerName: string;
  customerEmail: string;
}

export interface AdminOrderListResponse {
  orders: AdminOrder[];
  pagination: PaginationResponse;
}

export interface OrderDetail extends Order {}

export interface OrderDetailResponse {
  order: OrderDetail;
}

// ============================================
// AVALIAÇÕES
// ============================================

export interface BatteryReview {
  id: string;
  batteryId: string;
  userId: string;
  userName: string;
  rating: number;
  title: string;
  comment: string;
  images: string[];
  verifiedPurchase: boolean;
  helpful: number;
  createdAt: Date;
  updatedAt: Date;
}

export interface ReviewSummary {
  averageRating: number;
  totalReviews: number;
  ratingDistribution: {
    1: number;
    2: number;
    3: number;
    4: number;
    5: number;
  };
}

export interface ReviewListResponse {
  reviews: BatteryReview[];
  pagination: PaginationResponse;
  summary: ReviewSummary;
}

export interface AdminReview {
  id: string;
  customerName: string;
  customerEmail: string;
  productId: string;
  productName: string;
  rating: number;
  title: string;
  comment: string;
  helpfulVotes: number;
  status: 'pending' | 'approved' | 'rejected';
  verifiedPurchase: boolean;
  images?: string[];
  createdAt: string;
  updatedAt: string;
}

export interface AdminReviewSummary {
  total: number;
  pending: number;
  approved: number;
  rejected?: number;
  averageRating: number;
}

export interface AdminReviewListResponse {
  reviews: AdminReview[];
  pagination: PaginationResponse;
  summary?: AdminReviewSummary;
}

export interface CreateReviewRequest {
  rating: number;
  title: string;
  comment: string;
  images?: string[];
}

// ============================================
// FRETE
// ============================================

export interface ShippingOption {
  method: string;
  name: string;
  price: number;
  estimatedDays: number;
  description: string;
}

export interface CalculateShippingRequest {
  zipCode: string;
  items: {
    batteryId: string;
    quantity: number;
    weight: number;
  }[];
  shippingMethod?: string;
}

export interface CalculateShippingResponse {
  options: ShippingOption[];
}

export interface TrackingEvent {
  date: Date;
  status: string;
  location: string;
  description: string;
}

export interface TrackingInfo {
  code: string;
  status: string;
  events: TrackingEvent[];
  estimatedDelivery: Date;
}

// ============================================
// VEÍCULOS (FIPE)
// ============================================

export interface VehicleBrand {
  codigo: string;
  nome: string;
}

export interface VehicleModel {
  codigo: string;
  nome: string;
}

export interface VehicleYear {
  codigo: string;
  nome: string;
}

// ============================================
// PAGAMENTOS
// ============================================

export enum PaymentMethod {
  PIX = 'pix',
  CREDIT_CARD = 'credit_card',
  DEBIT_CARD = 'debit_card',
  BOLETO = 'boleto',
}

export enum PaymentStatus {
  PENDING = 'pending',
  PROCESSING = 'processing',
  AUTHORIZED = 'authorized',
  PAID = 'paid',
  COMPLETED = 'completed',
  REFUNDED = 'refunded',
  FAILED = 'failed',
  CANCELLED = 'cancelled',
  CANCELED = 'canceled',
  CHARGEBACK = 'chargeback',
}

export interface PixPaymentRequest {
  orderId: string;
  amount: number;
}

export interface PixPaymentResponse {
  pixCode: string;
  qrCode: string;
  expiresAt: Date;
}

export interface CreditCardData {
  number: string;
  holderName: string;
  expiryMonth: string;
  expiryYear: string;
  cvv: string;
}

export interface CreditCardPaymentRequest {
  orderId: string;
  amount: number;
  installments: number;
  cardData: CreditCardData;
}

export interface CreditCardPaymentResponse {
  transactionId: string;
  status: string;
  authorizationCode: string;
}

export interface PaymentTransaction {
  id: string;
  orderId: string;
  transactionId: string;
  paymentMethod: PaymentMethod | string;
  amount: number;
  status: PaymentStatus | string;
  authorizationCode?: string;
  installments: number;
  pixCode?: string;
  boletoUrl?: string;
  boletoBarcode?: string;
  dueDate?: string;
  paidAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface BoletoPaymentRequest {
  orderId: string;
  amount: number;
}

export interface BoletoPaymentResponse {
  boletoUrl: string;
  barcode: string;
  dueDate: Date;
}

// ============================================
// ADMIN / ESTATÍSTICAS
// ============================================

export interface DashboardStats {
  totalOrders: number;
  totalRevenue: number;
  totalCustomers: number;
  totalProducts: number;
  recentOrders: DashboardRecentOrder[];
  topProducts: DashboardTopProduct[];
  salesChart: DashboardSalesPoint[];
  lowStockProducts: DashboardLowStockProduct[];
}

export interface ProductListResponse {
  products: Battery[];
  pagination: PaginationResponse;
  filters: ProductFiltersResponse;
}

export interface ProductFiltersResponse {
  brands: string[];
  categories: string[];
  priceRange: {
    min: number;
    max: number;
  };
}

export interface DashboardRecentOrder {
  id: string;
  orderNumber: string | null;
  customerName: string;
  customerEmail: string;
  total: number;
  status: string;
  firstItem: string;
  createdAt: string;
}

export interface DashboardTopProduct {
  id: string;
  name: string;
  brand?: string | null;
  imageUrl?: string | null;
  sales: number;
  revenue: number;
}

export interface DashboardLowStockProduct {
  id: string;
  name: string;
  brand?: string | null;
  stock: number;
  minStock: number;
  price: number;
}

export interface DashboardSalesPoint {
  date: string;
  sales: number;
}

export type AdminReportRange = '7' | '30' | '90' | '365' | 'custom';
export type AdminReportType = 'sales' | 'products' | 'customers' | 'inventory';

export interface AdminReportSummary {
  totalRevenue: number;
  totalOrders: number;
  averageOrderValue: number;
  conversionRate: number;
  revenueGrowth: number;
  ordersGrowth: number;
  averageOrderValueGrowth: number;
  conversionRateGrowth: number;
}

export interface AdminReportSalesPoint {
  date: string;
  revenue: number;
  orders: number;
}

export interface AdminReportProduct {
  id: string;
  name: string;
  brand?: string | null;
  imageUrl?: string | null;
  revenue: number;
  sales: number;
}

export interface AdminReportCustomer {
  id: string;
  name: string;
  email: string;
  orders: number;
  total: number;
}

export interface AdminCustomerAddress {
  street?: string | null;
  number?: string | null;
  complement?: string | null;
  neighborhood?: string | null;
  city?: string | null;
  state?: string | null;
  zipCode?: string | null;
}

export interface AdminCustomer {
  id: string;
  name: string;
  email: string;
  phone?: string | null;
  cpf?: string | null;
  totalOrders: number;
  totalSpent: number;
  lastOrderDate?: string | null;
  registrationDate?: string | null;
  isActive: boolean;
  address?: AdminCustomerAddress | null;
}

export interface AdminCustomerSummary {
  totalCustomers: number;
  activeCustomers: number;
  totalRevenue: number;
}

export interface AdminCustomerListResponse {
  customers: AdminCustomer[];
  pagination: PaginationResponse;
  summary?: AdminCustomerSummary;
}

export interface AdminReportsData {
  summary: AdminReportSummary;
  salesTrend: AdminReportSalesPoint[];
  topProducts: AdminReportProduct[];
  topCustomers: AdminReportCustomer[];
}

export interface GetAdminReportsParams {
  range?: AdminReportRange;
  type?: AdminReportType;
  startDate?: string;
  endDate?: string;
}

// ============================================
// BUSCA
// ============================================

export interface SearchSuggestion {
  type: string;
  id: string;
  title: string;
  description: string;
  url: string;
}

export interface AutocompleteResponse {
  results: SearchSuggestion[];
}

